// Security update: HTML rendered from stored content is sanitized with sanitizeHtml() before it is inserted into the page.
/* Blog Detail Client Component - Client-side rendering for blog detail page
   Updated: Fetches blog data on mount, supports markdown rendering, added React Helmet for dynamic SEO
   Updated: Added specific h2 (text-4xl) and h3 (text-2xl) sizes for better hierarchy, custom border radius on featured image
   Updated: Added read progress bar at bottom of sticky navbar
   Updated: Automatically increments view count when blog is loaded */



import { sanitizeHtml } from '@/lib/sanitize-html';
import { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { supabase } from '@/lib/supabase';
import { getBlogImageUrl } from '@/lib/image-url';
import ContactBar from '@/components/ContactBar';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import PageBreadcrumb from '@/components/PageBreadcrumb';
import { Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, Loader as Loader2 } from 'lucide-react';
import { marked } from 'marked';
import { trackGoal } from '@/lib/datafast';

marked.setOptions({
  breaks: true,
  gfm: true,
  pedantic: false,
});

// Configure marked renderer to add target="_blank" to all links
const renderer = new marked.Renderer();
const originalLinkRenderer = renderer.link.bind(renderer);
renderer.link = (href: string, title: string | null | undefined, text: string) => {
  const html = originalLinkRenderer(href, title, text);
  return html.replace('<a', '<a target="_blank" rel="noopener noreferrer"');
};
marked.use({ renderer });

interface Blog {
  id: string;
  title: string;
  slug: string;
  excerpt: string;
  image_url: string;
  image_storage_path?: string;
  published_at: string;
  content: string;
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
  og_image_storage_path?: string;
}

export default function BlogDetailClient() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState<Blog | null>(null);
  const [loading, setLoading] = useState(true);
  const [readProgress, setReadProgress] = useState(0);

  const htmlContent = useMemo(() => {
    if (!blog?.content) return '';
    try {
      let content = blog.content;
      content = content.replace(/(<\/[^>]+>)\n(?!\n)/g, '$1\n\n');
      content = content.replace(/(<\/[^>]+>)([^\n<])/g, '$1\n\n$2');
      return sanitizeHtml(marked.parse(content) as string);
    } catch (error) {
      return blog.content;
    }
  }, [blog?.content]);

  useEffect(() => {
    const handleScroll = () => {
      const windowHeight = window.innerHeight;
      const documentHeight = document.documentElement.scrollHeight;
      const scrollTop = window.scrollY;

      const scrollableHeight = documentHeight - windowHeight;
      const progress = (scrollTop / scrollableHeight) * 100;

      setReadProgress(Math.min(100, Math.max(0, progress)));
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchBlog = async () => {
      if (!slug) {
        return;
      }

      try {
        setLoading(true);

        const now = new Date().toISOString();
        const { data, error } = await supabase
          .from('blogs')
          .select('*')
          .eq('slug', slug)
          .eq('published', true)
          .or(`scheduled_at.is.null,scheduled_at.lte.${now}`)
          .maybeSingle();

        if (error) {
          throw error;
        }

        if (!data) {
          navigate('/blogs');
          return;
        }

        setBlog(data);
        trackGoal('blog_viewed', { title: data.title });

        // Increment view count in background
        supabase.rpc('increment_blog_views', { blog_id: data.id });
      } catch (error) {
        navigate('/blogs');
      } finally {
        setLoading(false);
      }
    };

    fetchBlog();
  }, [slug, navigate]);

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString('nl-NL', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const estimateReadTime = (content: string) => {
    const wordsPerMinute = 200;
    const words = content.trim().split(/\s+/).length;
    return Math.ceil(words / wordsPerMinute);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <ContactBar />
        <Header />
        <div className="flex items-center justify-center pt-0 pb-">
          <Loader2 className="h-12 w-12 animate-spin text-jv-primary" />
        </div>
        <Footer />
      </div>
    );
  }

  if (!blog) {
    return (
      <div className="min-h-screen bg-white">
        <ContactBar />
        <Header />
        <div className="flex flex-col items-center justify-center pt-0 pb-">
          <h1 className="text-4xl font-bold text-jv-dark mb-4">Blog niet gevonden</h1>
          <Link to="/blogs" className="text-jv-primary hover:text-jv-accent">
            Terug naar blogs
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const getImageUrl = (storagePath?: string, fallbackUrl?: string) => {
    if (storagePath) {
      return getBlogImageUrl(storagePath, 'large');
    }
    return fallbackUrl || 'https://buitnplanten.nl/assets/sections/blogs.png';
  };

  const pageTitle = blog.meta_title || `${blog.title} | Buitn Planten`;
  const pageDescription = blog.meta_description || blog.excerpt;
  const pageImage = getImageUrl(blog.og_image_storage_path, blog.og_image) || getImageUrl(blog.image_storage_path, blog.image_url);
  const pageUrl = `https://buitnplanten.nl/blogs/${blog.slug}`;

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content={`${blog.title}, haagplanten, tuinplanten, Buitn Planten, Dedemsvaart, tuinieren, blog`} />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href={pageUrl} />

        {/* Open Graph */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={pageImage} />
        <meta property="article:published_time" content={blog.published_at} />

        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={pageUrl} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={pageImage} />

        {/* Article Schema */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": blog.title,
            "description": pageDescription,
            "image": pageImage,
            "datePublished": blog.published_at,
            "author": {
              "@type": "Person",
              "name": "Jelte Vrieling",
              "url": "https://buitnplanten.nl"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Buitn Planten",
              "logo": {
                "@type": "ImageObject",
                "url": "https://buitnplanten.nl/assets/laurier-primary.webp"
              }
            },
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": pageUrl
            }
          })}
        </script>
      </Helmet>

      <ContactBar />
      <Header />

      {/* Read Progress Bar */}
      <div className="fixed top-[88px] left-0 right-0 h-[2px] bg-gray-200 z-40">
        <div
          className="h-full bg-jv-primary transition-all duration-150 ease-out"
          style={{ width: `${readProgress}%` }}
        />
      </div>

      <main className="pt-8 pb-16 bg-white">
        <article className="max-w-4xl mx-auto px-8 sm:px-12 lg:px-16">
          <PageBreadcrumb items={[{ label: 'Home', href: '/' }, { label: 'Inspiratie', href: '/blogs' }, { label: blog.title }]} />
          {/* Back Button */}
          <Link
            to="/blogs"
            className="inline-flex items-center gap-2 text-jv-primary hover:text-jv-accent transition-colors duration-300 mb-8 group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
            <span className="font-semibold">Terug naar blogs</span>
          </Link>

          {/* Meta Information */}
          <div className="flex items-center gap-4 text-sm text-gray-600 mb-6">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              <span>{formatDate(blog.published_at)}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>{estimateReadTime(blog.content)} min lezen</span>
            </div>
          </div>

          {/* Title */}
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-jv-primary mb-6 leading-tight">
            {blog.title}
          </h1>

          {/* Excerpt */}
          <p className="text-xl text-gray-600 mb-8 leading-relaxed">
            {blog.excerpt}
          </p>

          {/* Featured Image */}
          <div className="relative h-[400px] md:h-[500px] rounded-tl-[72px] rounded-br-[72px] overflow-hidden bg-jv-tertiary shadow-lg mb-12">
            <img
              src={getImageUrl(blog.image_storage_path, blog.image_url)}
              alt={blog.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Blog Content */}
          <div
            className="prose prose-lg max-w-none prose-headings:text-jv-primary prose-headings:font-bold prose-headings:mb-4 prose-headings:mt-8 prose-h2:text-4xl prose-h2:leading-tight prose-h3:text-2xl prose-h3:leading-snug prose-p:text-gray-700 prose-p:leading-relaxed prose-p:mb-6 prose-a:text-jv-accent prose-a:no-underline hover:prose-a:underline prose-strong:text-jv-primary prose-strong:font-semibold prose-ul:text-gray-700 prose-ul:my-6 prose-ol:text-gray-700 prose-ol:my-6 prose-li:my-2 prose-img:rounded-xl prose-img:shadow-lg prose-img:my-8"
            dangerouslySetInnerHTML={{ __html: htmlContent }}
          />

          {/* Back Button at Bottom */}
          <div className="mt-16 pt-8 border-t border-gray-200">
            <Link
              to="/blogs"
              className="inline-flex items-center gap-2 text-jv-primary hover:text-jv-accent transition-colors duration-300 group"
            >
              <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform duration-300" />
              <span className="font-semibold">Terug naar alle blogs</span>
            </Link>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}
